import { useState, useEffect, useMemo } from "react";
import { 
  getAllStudents, subscribeProgressLogs, subscribeCategories, 
  DEFAULT_CATEGORIES, getStudentTargets, getStudentTaskTargets,
  setStudentTarget, setStudentTaskTarget
} from "../firebase/database";
import { todayStr, minutesToHHMM, HHMMToMinutes, getWeekRange, getMonthRange, getYearMonths, formatDate } from "../utils/helpers";
import { theme } from "../utils/theme";
import { useAuth } from "../context/AuthContext";
import { addDays, subDays, addMonths, subMonths, addYears, subYears, format } from "date-fns";

const C = theme.colors;

export default function LogTimelineReport({ studentId: kioskStudentId }) {
  const { user, userData } = useAuth();
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(kioskStudentId || "");
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("activity"); // "activity" | "summary" | "weekly" | "monthly" | "annual"

  const [catTargets, setCatTargets] = useState({});
  const [taskTargets, setTaskTargets] = useState({});
  const [editingTarget, setEditingTarget] = useState(null); // { type: 'cat'|'task', id: string, name: string }

  // Sorting state for summary tables
  const [catSort, setCatSort] = useState({ key: "minutes", direction: "desc" });
  const [taskSort, setTaskSort] = useState({ key: "minutes", direction: "desc" });

  // Selections for graphs
  const [selectType, setSelectType] = useState("category"); // "category" | "task"
  const [selectId, setSelectId] = useState("");

  // Filter for Task Breakdown table by category
  const [taskCategoryFilter, setTaskCategoryFilter] = useState("");

  // Reset task category filter when student or tab changes
  useEffect(() => {
    setTaskCategoryFilter("");
  }, [selectedStudentId, activeTab]);

  const isFullAdmin = userData?.role === "admin" || userData?.role === "trainer";

  useEffect(() => {
    const unsubCats = subscribeCategories(setCategories);
    return () => unsubCats();
  }, []);

  const isKiosk = !!kioskStudentId;

  useEffect(() => {
    if (isKiosk) {
      setSelectedStudentId(kioskStudentId);
      setLoading(false);
      return;
    }

    getAllStudents().then(list => {
      const activeList = list.filter(s => s.status !== "inactive");
      setStudents(activeList);

      if (isFullAdmin) {
        // Admin/Trainer: Default to Pankaj or first student only if nothing is selected yet
        if (!selectedStudentId) {
          const defaultStudent = activeList.find(s => (s.name === "Pankaj Ojha" && (s.roll === "12" || s.roll === "012")));
          if (defaultStudent) {
            setSelectedStudentId(defaultStudent.id);
          } else if (activeList.length > 0) {
            setSelectedStudentId(activeList[0].id);
          }
        }
      } else {
        // Instructor/Staff/Student: Default to their own profile
        const selfId = userData?.studentId || user?.uid;
        if (selfId && !selectedStudentId) {
          setSelectedStudentId(selfId);
        }
      }
      setLoading(false);
    });
  }, [isKiosk, kioskStudentId, userData, user, selectedStudentId, isFullAdmin]);

  useEffect(() => {
    if (!selectedStudentId) return;
    getStudentTargets(selectedStudentId).then(setCatTargets);
    getStudentTaskTargets(selectedStudentId).then(setTaskTargets);
    
    const unsubProgress = subscribeProgressLogs(selectedStudentId, (logs) => {
      setAllLogs(logs);
    });
    
    return unsubProgress;
  }, [selectedStudentId]);

  // ranges
  const weekRange  = useMemo(() => getWeekRange(selectedDate), [selectedDate]);
  const monthRange = useMemo(() => getMonthRange(selectedDate), [selectedDate]);
  const yearMonths = useMemo(() => getYearMonths(selectedDate), [selectedDate]);
  
  // filtered logs
  const dailyLogs  = useMemo(() => allLogs.filter(l => l.date === selectedDate), [allLogs, selectedDate]);
  const weeklyLogs = useMemo(() => allLogs.filter(l => weekRange.includes(l.date)), [allLogs, weekRange]);
  const monthlyLogs = useMemo(() => allLogs.filter(l => monthRange.includes(l.date)), [allLogs, monthRange]);
  const annualLogs  = useMemo(() => allLogs.filter(l => l.date.startsWith(selectedDate.slice(0, 4))), [allLogs, selectedDate]);

  // summary calculator
  const getSummary = (logs) => {
    const catMap = {};
    const taskMap = {};
    categories.forEach(c => catMap[c.id] = { minutes: 0, logs: [] });
    logs.forEach(log => {
      const catId = log.category;
      if (!catMap[catId]) {
        catMap[catId] = { minutes: 0, logs: [] };
      }
      catMap[catId].minutes += log.loggedMinutes;
      catMap[catId].logs.push(log);

      if (!taskMap[log.topicName]) {
        taskMap[log.topicName] = { minutes: 0, category: log.category, logs: [] };
      }
      taskMap[log.topicName].minutes += log.loggedMinutes;
      taskMap[log.topicName].logs.push(log);
    });
    return { categories: catMap, tasks: taskMap };
  };

  const currentSummary = useMemo(() => {
    if (activeTab === "summary") return getSummary(dailyLogs);
    if (activeTab === "weekly") return getSummary(weeklyLogs);
    if (activeTab === "monthly") return getSummary(monthlyLogs);
    if (activeTab === "annual") return getSummary(annualLogs);
    return { categories: {}, tasks: {} };
  }, [activeTab, dailyLogs, weeklyLogs, monthlyLogs, annualLogs, categories]);

  // Set default selection
  useEffect(() => {
    if (["weekly", "monthly", "annual"].includes(activeTab) && !selectId) {
      if (selectType === "category" && categories.length > 0) {
        setSelectId(categories[0].id);
      } else if (selectType === "task") {
        const firstTask = Object.keys(currentSummary.tasks)[0];
        if (firstTask) setSelectId(firstTask);
      }
    }
  }, [activeTab, selectType, categories, currentSummary.tasks, selectId]);

  const handleSaveTarget = async (type, id, hhmm) => {
    if (type === 'cat') {
      await setStudentTarget(selectedStudentId, id, hhmm);
      setCatTargets(prev => ({ ...prev, [id]: { targetMinutes: HHMMToMinutes(hhmm) } }));
    } else {
      await setStudentTaskTarget(selectedStudentId, id, hhmm);
      setTaskTargets(prev => ({ ...prev, [id]: { targetMinutes: HHMMToMinutes(hhmm) } }));
    }
    setEditingTarget(null);
  };

  const shiftTime = (direction) => {
    const current = new Date(selectedDate + "T00:00:00");
    let next;
    if (activeTab === "activity" || activeTab === "summary") {
      next = direction === "next" ? addDays(current, 1) : subDays(current, 1);
    } else if (activeTab === "weekly") {
      next = direction === "next" ? addDays(current, 7) : subDays(current, 7);
    } else if (activeTab === "monthly") {
      next = direction === "next" ? addMonths(current, 1) : subMonths(current, 1);
    } else if (activeTab === "annual") {
      next = direction === "next" ? addYears(current, 1) : subYears(current, 1);
    }
    if (next) setSelectedDate(format(next, "yyyy-MM-dd"));
  };

  const timelineLogs = useMemo(() => {
    const sorted = [...dailyLogs].sort((a, b) => HHMMToMinutes(a.startTime) - HHMMToMinutes(b.startTime));
    const rows = [];
    sorted.forEach(log => {
      const start = HHMMToMinutes(log.startTime);
      let placed = false;
      for (let i = 0; i < rows.length; i++) {
        const lastInRow = rows[i][rows[i].length - 1];
        if (HHMMToMinutes(lastInRow.endTime) <= start) {
          rows[i].push(log);
          placed = true;
          break;
        }
      }
      if (!placed) rows.push([log]);
    });
    return rows;
  }, [dailyLogs]);

  const hours = Array.from({ length: 25 }, (_, i) => i);

  // Line Chart Helper
  function LineChart({ dataPoints, labels, targetMin, multiplier = 1, showTarget = true }) {
    const maxLogged = Math.max(...dataPoints, 0);
    const dailyTarget = targetMin;
    const yScaleMax = Math.max(maxLogged, dailyTarget, 60) * 1.2;
    const color = selectType === "category" ? (categories.find(c => c.id === selectId)?.color || C.accent) : C.accent;
    const width = 700;
    const height = 200;

    return (
      <div style={{ display: "flex", gap: 15 }}>
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: height, paddingBottom: 25, fontSize: 10, color: C.muted, textAlign: "right", width: 45 }}>
          <span>{minutesToHHMM(yScaleMax)}</span>
          <span>{minutesToHHMM(yScaleMax * 0.75)}</span>
          <span>{minutesToHHMM(yScaleMax * 0.5)}</span>
          <span>{minutesToHHMM(yScaleMax * 0.25)}</span>
          <span>00:00</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ height: height, position: "relative", background: "#fbfcfd", borderBottom: `1px solid ${C.dim}`, borderLeft: `1px solid ${C.dim}`, padding: "10px 0" }}>
            <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ overflow: "visible" }}>
              {[0.25, 0.5, 0.75, 1].map(p => (
                <line key={p} x1="0" y1={height - p * height} x2={width} y2={height - p * height} stroke={C.dim} strokeWidth="1" strokeDasharray="4" />
              ))}
              {showTarget && dailyTarget > 0 && (
                <line 
                  x1="0" y1={height - (dailyTarget / yScaleMax) * height} 
                  x2={width} y2={height - (dailyTarget / yScaleMax) * height} 
                  stroke="#FF4D4D" strokeWidth="2" strokeDasharray="6,4" 
                />
              )}
              <polyline
                fill="none"
                stroke={color}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={dataPoints.map((v, i) => `${(i / (dataPoints.length - 1 || 1)) * width},${height - (v / yScaleMax) * height}`).join(" ")}
              />
              {dataPoints.map((v, i) => {
                const cx = (i / (dataPoints.length - 1 || 1)) * width;
                const cy = height - (v / yScaleMax) * height;
                return (
                  <g key={i}>
                    <circle cx={cx} cy={cy} r={dataPoints.length > 15 ? "3" : "5"} fill={color} stroke="#fff" strokeWidth="2" />
                    {v > 0 && dataPoints.length < 32 && (
                      <text x={cx} y={cy - 12} textAnchor="middle" fontSize="9" fontWeight="700" fill={C.text}>
                        {minutesToHHMM(v)}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            {labels.map((label, i) => (
              <div key={i} style={{ fontSize: 9, fontWeight: 600, color: C.muted, textAlign: "center", width: `${100/labels.length}%` }}>
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Pie Chart Helper
  function PieChart({ data }) {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return null;

    // Sort data descending by value
    const sortedData = [...data].sort((a, b) => b.value - a.value);

    let cumulativeAngle = 0;
    const radius = 80;
    const centerX = 100;
    const centerY = 100;

    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 40, padding: "20px 0", flexWrap: "wrap" }}>
        <svg width="200" height="200" viewBox="0 0 200 200">
          {sortedData.map((item, index) => {
            if (item.value === 0) return null;
            const angle = (item.value / total) * 360;
            const largeArcFlag = angle > 180 ? 1 : 0;
            
            const startAngle = cumulativeAngle;
            const endAngle = cumulativeAngle + angle;
            
            const x1 = centerX + radius * Math.cos((startAngle - 90) * Math.PI / 180);
            const y1 = centerY + radius * Math.sin((startAngle - 90) * Math.PI / 180);
            const x2 = centerX + radius * Math.cos((endAngle - 90) * Math.PI / 180);
            const y2 = centerY + radius * Math.sin((endAngle - 90) * Math.PI / 180);
            
            const d = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
            
            cumulativeAngle += angle;
            
            return (
              <path key={index} d={d} fill={item.color} stroke="#fff" strokeWidth="2">
                <title>{`${item.label}: ${Math.round((item.value / total) * 100)}%`}</title>
              </path>
            );
          })}
          <circle cx={centerX} cy={centerY} r={radius * 0.45} fill="#fff" />
        </svg>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 150 }}>
          {sortedData.map((item, index) => (
            <div key={index} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: item.color }} />
                <span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{item.label}</span>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>{Math.round((item.value / total) * 100)}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const SortIcon = ({ currentKey, sortKey, direction }) => {
    if (currentKey !== sortKey) return <span style={{ color: C.dim, marginLeft: 4, fontSize: 10 }}>↕</span>;
    return <span style={{ color: C.accent, marginLeft: 4, fontSize: 10 }}>{direction === "asc" ? "↑" : "↓"}</span>;
  };

  const SummaryTables = ({ summary, targetDays = 7 }) => {
    const handleSortCat = (key) => {
      setCatSort(prev => ({
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
      }));
    };

    const handleSortTask = (key) => {
      setTaskSort(prev => ({
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
      }));
    };

    const sortedCats = Object.keys(summary.categories).map(catId => {
      const cat = categories.find(c => c.id === catId) || { id: catId, label: catId, color: C.muted };
      const total = summary.categories[catId]?.minutes || 0;
      const target = (catTargets[catId]?.targetMinutes || 0) * targetDays;
      const progress = target > 0 ? Math.round((total / target) * 100) : 0;
      return { id: catId, label: cat.label, minutes: total, target, progress, color: cat.color };
    }).sort((a, b) => {
      let valA = a[catSort.key];
      let valB = b[catSort.key];
      if (typeof valA === 'string') { valA = valA.toLowerCase(); valB = valB.toLowerCase(); }
      if (valA < valB) return catSort.direction === "asc" ? -1 : 1;
      if (valA > valB) return catSort.direction === "asc" ? 1 : -1;
      return 0;
    });

    const sortedTasks = Object.entries(summary.tasks).map(([name, data]) => {
      const target = (taskTargets[name]?.targetMinutes || 0) * targetDays;
      return { name, minutes: data.minutes, target, categoryId: data.category };
    }).filter(item => {
      if (!taskCategoryFilter) return true;
      return item.categoryId === taskCategoryFilter;
    }).sort((a, b) => {
      let valA = a[taskSort.key];
      let valB = b[taskSort.key];
      if (typeof valA === 'string') { valA = valA.toLowerCase(); valB = valB.toLowerCase(); }
      if (valA < valB) return taskSort.direction === "asc" ? -1 : 1;
      if (valA > valB) return taskSort.direction === "asc" ? 1 : -1;
      return 0;
    });

    return (
      <div className="summary-grid">
        <div className="table-wrapper">
          <div style={{ padding: "12px 16px", background: "#F9FAFB", borderBottom: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700 }}>Category Summary</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fff", borderBottom: `1px solid ${C.border}` }}>
                <th onClick={() => handleSortCat("label")} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, color: C.muted, cursor: "pointer", userSelect: "none" }}>
                  Category <SortIcon currentKey={catSort.key} sortKey="label" direction={catSort.direction} />
                </th>
                <th onClick={() => handleSortCat("minutes")} style={{ padding: "10px 16px", textAlign: "right", fontSize: 11, color: C.muted, cursor: "pointer", userSelect: "none" }}>
                  Logged <SortIcon currentKey={catSort.key} sortKey="minutes" direction={catSort.direction} />
                </th>
                <th onClick={() => handleSortCat("target")} style={{ padding: "10px 16px", textAlign: "right", fontSize: 11, color: C.muted, cursor: "pointer", userSelect: "none" }}>
                  Target ({targetDays}d) <SortIcon currentKey={catSort.key} sortKey="target" direction={catSort.direction} />
                </th>
                <th onClick={() => handleSortCat("progress")} style={{ padding: "10px 16px", textAlign: "right", fontSize: 11, color: C.muted, cursor: "pointer", userSelect: "none" }}>
                  % <SortIcon currentKey={catSort.key} sortKey="progress" direction={catSort.direction} />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedCats.map(item => {
                const isSelected = selectType === "category" && selectId === item.id;
                const isFiltering = taskCategoryFilter === item.id;
                return (
                  <tr key={item.id} onClick={() => { 
                    setSelectType("category"); 
                    setSelectId(item.id); 
                    setTaskCategoryFilter(item.id);
                  }} style={{ borderBottom: `1px solid ${C.dim}`, cursor: "pointer", background: isFiltering ? C.accentSoft : isSelected ? "#f0f7ff" : "transparent" }}>
                    <td style={{ padding: "10px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: item.color }} />
                        <span style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400 }}>{item.label}</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 16px", textAlign: "right", fontSize: 13, fontWeight: 600 }}>{minutesToHHMM(item.minutes)}</td>
                    <td style={{ padding: "10px 16px", textAlign: "right", fontSize: 13, color: C.muted }}>{item.target > 0 ? minutesToHHMM(item.target) : "—"}</td>
                    <td style={{ padding: "10px 16px", textAlign: "right", fontSize: 13, fontWeight: 700, color: item.progress >= 100 ? C.green : item.progress >= 75 ? C.accent : C.red }}>{item.target > 0 ? `${item.progress}%` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: "#F9FAFB", borderBottom: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Task Breakdown {taskCategoryFilter && <span style={{ color: C.accent, fontWeight: 400, marginLeft: 8 }}>(Filtered by {categories.find(c => c.id === taskCategoryFilter)?.label})</span>}</span>
            {taskCategoryFilter && (
              <button onClick={() => setTaskCategoryFilter("")} style={{ background: "none", border: "none", color: C.red, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>✕ Clear Filter</button>
            )}
          </div>
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fff", borderBottom: `1px solid ${C.border}` }}>
                  <th onClick={() => handleSortTask("name")} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, color: C.muted, cursor: "pointer", userSelect: "none" }}>
                    Task <SortIcon currentKey={taskSort.key} sortKey="name" direction={taskSort.direction} />
                  </th>
                  <th onClick={() => handleSortTask("minutes")} style={{ padding: "10px 16px", textAlign: "right", fontSize: 11, color: C.muted, cursor: "pointer", userSelect: "none" }}>
                    Logged <SortIcon currentKey={taskSort.key} sortKey="minutes" direction={taskSort.direction} />
                  </th>
                  <th onClick={() => handleSortTask("target")} style={{ padding: "10px 16px", textAlign: "right", fontSize: 11, color: C.muted, cursor: "pointer", userSelect: "none" }}>
                    Target ({targetDays}d) <SortIcon currentKey={taskSort.key} sortKey="target" direction={taskSort.direction} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedTasks.map((item) => {
                  const isSelected = selectType === "task" && selectId === item.name;
                  return (
                    <tr key={item.name} onClick={() => { setSelectType("task"); setSelectId(item.name); }} style={{ borderBottom: `1px solid ${C.dim}`, cursor: "pointer", background: isSelected ? C.accentSoft : "transparent" }}>
                      <td style={{ padding: "10px 16px" }}>
                        <div style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400 }}>{item.name}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>{categories.find(c => c.id === item.categoryId)?.label}</div>
                      </td>
                      <td style={{ padding: "10px 16px", textAlign: "right", fontSize: 13, fontWeight: 600 }}>{minutesToHHMM(item.minutes)}</td>
                      <td style={{ padding: "10px 16px", textAlign: "right", fontSize: 13, color: C.muted }}>{item.target > 0 ? minutesToHHMM(item.target) : "—"}</td>
                    </tr>
                  );
                })}
                {sortedTasks.length === 0 && <tr><td colSpan="3" style={{ padding: 30, textAlign: "center", color: C.muted }}>No tasks logged.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div style={{ padding: 20, textAlign: "center" }}>Loading report…</div>;

  return (
    <div style={{ fontFamily: "DM Sans, sans-serif" }}>
      <style>{`
        .report-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; gap: 16px; }
        .header-controls { display: flex; gap: 12px; align-items: center; }
        .summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 20px; }
        .chart-container { background: #fff; border: 1px solid ${C.border}; border-radius: 12px; padding: 20px; margin-bottom: 24px; overflow-x: auto; }
        .table-wrapper { background: #fff; border: 1px solid ${C.border}; border-radius: 12px; overflow-x: auto; }
        
        @media (max-width: 768px) {
          .report-header { flex-direction: column; align-items: flex-start; }
          .header-controls { width: 100%; justify-content: space-between; flex-wrap: wrap; }
          .summary-grid { grid-template-columns: 1fr; }
          .timeline-container { padding: 40px 10px 10px !important; }
        }
        @media (max-width: 480px) {
          .header-controls > * { flex: 1; min-width: 140px; }
        }
      `}</style>

      <div className="report-header">
        <div>
          <div style={{ fontSize: isKiosk ? 20 : 24, fontWeight: 700, color: C.text }}>{isKiosk ? "My Progress Report" : "Log Timeline Report"}</div>
          <div style={{ fontSize: 14, color: C.muted }}>{isKiosk ? "Track your activity performance" : "Detailed progress visualizations"}</div>
        </div>
        <div className="header-controls">
          {isFullAdmin && !isKiosk ? (
            <select value={selectedStudentId} onChange={e => setSelectedStudentId(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", outline: "none", fontSize: 14 }}>
              <option value="">Select Student</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.name} ({s.roll})</option>)}
            </select>
          ) : !isKiosk && (
            <div style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 14, fontWeight: 600, color: C.text }}>
              {students.find(s => s.id === selectedStudentId)?.name || userData?.name || "My Progress"}
            </div>
          )}
          <div style={{ display: "flex", background: "#fff", border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
            <button onClick={() => shiftTime("prev")} style={{ padding: "8px 12px", border: "none", background: "none", cursor: "pointer", color: C.accent, fontWeight: 700 }}>&lt;</button>
            <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{ padding: "8px 4px", border: "none", background: "none", outline: "none", fontSize: 14, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}` }} />
            <button onClick={() => shiftTime("next")} style={{ padding: "8px 12px", border: "none", background: "none", cursor: "pointer", color: C.accent, fontWeight: 700 }}>&gt;</button>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { id: "activity", label: "Daily Activity" },
          { id: "summary", label: "Daily Summary" },
          { id: "weekly", label: "Weekly" },
          { id: "monthly", label: "Monthly" },
          { id: "annual", label: "Annual" }
        ].map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSelectId(""); }} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: activeTab === tab.id ? C.accent : C.dim, color: activeTab === tab.id ? "#fff" : C.text, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === "activity" && (
        <>
          <div className="timeline-container" style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: "40px 20px 20px", position: "relative", overflowX: "auto" }}>
            <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, paddingBottom: 10, marginBottom: 20, minWidth: 600 }}>
              {hours.map(h => <div key={h} style={{ flex: 1, textAlign: "left", fontSize: 11, color: C.muted, borderLeft: `1px solid ${C.dim}`, paddingLeft: 4, height: 10 }}>{String(h).padStart(2, "0")}:00</div>)}
            </div>
            <div style={{ position: "relative", minHeight: Math.max(200, timelineLogs.length * 50), minWidth: 600 }}>
              {hours.map(h => <div key={h} style={{ position: "absolute", left: `${(h / 24) * 100}%`, top: 0, bottom: 0, width: 1, background: C.dim, opacity: 0.5, zIndex: 0 }} />)}
              {timelineLogs.map((row, rowIndex) => (
                <div key={rowIndex} style={{ position: "relative", height: 40, marginBottom: 10 }}>
                  {row.map(log => {
                    const startMin = HHMMToMinutes(log.startTime);
                    const endMin = HHMMToMinutes(log.endTime);
                    const left = (startMin / 1440) * 100;
                    const width = ((endMin - startMin) / 1440) * 100;
                    const cat = categories.find(c => c.id === log.category);
                    const color = cat?.color || C.accent;
                    return (
                      <div key={log.entryId} title={`${log.topicName} (${log.startTime} - ${log.endTime})`} style={{ position: "absolute", left: `${left}%`, width: `${width}%`, height: "100%", background: color, borderRadius: 6, border: "1px solid rgba(0,0,0,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 600, overflow: "hidden", whiteSpace: "nowrap", padding: "0 4px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", zIndex: 1, cursor: "help" }}>{log.topicName}</div>
                    );
                  })}
                </div>
              ))}
              {dailyLogs.length === 0 && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 14 }}>No logs found.</div>}
            </div>
            <div style={{ display: "flex", borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 20, minWidth: 600 }}>
              {hours.map(h => <div key={h} style={{ flex: 1, textAlign: "left", fontSize: 11, color: C.muted, borderLeft: `1px solid ${C.dim}`, paddingLeft: 4, height: 10 }}>{String(h).padStart(2, "0")}:00</div>)}
            </div>
          </div>
          <div style={{ marginTop: 24, display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 32 }}>
            {categories.map(cat => {
              const target = catTargets[cat.id]?.targetMinutes;
              return (
                <div key={cat.id} style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", padding: "6px 12px", borderRadius: 20, border: `1px solid ${C.border}` }}>
                  <div style={{ width: 12, height: 12, borderRadius: "50%", background: cat.color }} />
                  <span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{cat.label}</span>
                  {target ? <span style={{ fontSize: 11, color: C.muted }}>Daily: {minutesToHHMM(target)}</span> : null}
                  {!isKiosk && <button onClick={() => setEditingTarget({ type: 'cat', id: cat.id, name: cat.label, value: minutesToHHMM(target || 0) })} style={{ background: "none", border: "none", color: C.accent, fontSize: 10, cursor: "pointer", fontWeight: 600 }}>✎</button>}
                </div>
              );
            })}
          </div>
          <div className="table-wrapper">
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
              <thead>
                <tr style={{ background: "#F9FAFB", borderBottom: `1px solid ${C.border}` }}>
                  {["Category", "Task Name", "Start", "End", "Duration", "Target (Daily)", "Progress", ...(isKiosk ? [] : ["Action"])].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: C.muted, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dailyLogs.sort((a,b) => HHMMToMinutes(a.startTime) - HHMMToMinutes(b.startTime)).map((log, i) => {
                  const cat = categories.find(c => c.id === log.category);
                  const duration = HHMMToMinutes(log.endTime) - HHMMToMinutes(log.startTime);
                  const target = taskTargets[log.topicName]?.targetMinutes || 0;
                  const progress = target > 0 ? Math.round((duration / target) * 100) : 0;
                  return (
                    <tr key={log.entryId} style={{ borderBottom: i < dailyLogs.length - 1 ? `1px solid ${C.border}` : "none" }}>
                      <td style={{ padding: "12px 16px" }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: cat?.color || C.accent }} /><span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{cat?.label || log.category}</span></div></td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: C.text }}>{log.topicName}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: C.muted, fontFamily: "monospace" }}>{log.startTime}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: C.muted, fontFamily: "monospace" }}>{log.endTime}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: C.text, fontWeight: 600 }}>{minutesToHHMM(duration)}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: C.muted }}>{target > 0 ? minutesToHHMM(target) : "—"}</td>
                      <td style={{ padding: "12px 16px" }}>{target > 0 ? <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ flex: 1, width: 60, height: 6, background: C.dim, borderRadius: 3, overflow: "hidden" }}><div style={{ width: `${Math.min(100, progress)}%`, height: "100%", background: progress >= 100 ? C.green : C.accent }} /></div><span style={{ fontSize: 11, fontWeight: 700, color: progress >= 100 ? C.green : C.accent }}>{progress}%</span></div> : "—"}</td>
                      {!isKiosk && <td style={{ padding: "12px 16px" }}><button onClick={() => setEditingTarget({ type: 'task', id: log.topicName, name: log.topicName, value: minutesToHHMM(target) })} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "#fff", color: C.accent, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Set Target</button></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === "summary" && (
        <>
          <div className="table-wrapper">
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 500 }}>
              <thead>
                <tr style={{ background: "#F9FAFB", borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: C.muted, fontWeight: 600 }}>Category</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: C.muted, fontWeight: 600 }}>Total Duration</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: C.muted, fontWeight: 600 }}>Daily Target</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, color: C.muted, fontWeight: 600 }}>Progress</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const summary = getSummary(dailyLogs);
                  const filteredCats = Object.keys(summary.categories).filter(catId => (summary.categories[catId]?.minutes || 0) > 0);
                  if (filteredCats.length === 0) {
                    return <tr><td colSpan="4" style={{ padding: 40, textAlign: "center", color: C.muted }}>No activity recorded for this day.</td></tr>;
                  }
                  return filteredCats.map(catId => {
                    const cat = categories.find(c => c.id === catId) || { id: catId, label: catId, color: C.muted };
                    const totalMinutes = summary.categories[catId]?.minutes || 0;
                    const target = catTargets[catId]?.targetMinutes || 0;
                    const progress = target > 0 ? Math.round((totalMinutes / target) * 100) : 0;
                    return (
                      <tr key={catId} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: "12px 16px" }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 10, height: 10, borderRadius: "50%", background: cat.color }} /><span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{cat.label}</span></div></td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: C.text, fontWeight: 600 }}>{minutesToHHMM(totalMinutes)}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: C.muted }}>{target > 0 ? minutesToHHMM(target) : "—"}</td>
                        <td style={{ padding: "12px 16px" }}>{target > 0 ? <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ flex: 1, width: 100, height: 8, background: C.dim, borderRadius: 4, overflow: "hidden" }}><div style={{ width: `${Math.min(100, progress)}%`, height: "100%", background: progress >= 100 ? C.green : C.accent }} /></div><span style={{ fontSize: 12, fontWeight: 700, color: progress >= 100 ? C.green : C.accent }}>{progress}%</span></div> : <span style={{ color: C.muted }}>—</span>}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>

          {dailyLogs.length > 0 && (
            <div className="chart-container" style={{ marginTop: 24, textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 16 }}>Category Distribution</div>
              <PieChart 
                data={Object.keys(currentSummary.categories)
                  .map(catId => {
                    const cat = categories.find(c => c.id === catId) || { label: catId, color: C.muted };
                    return {
                      label: cat.label,
                      value: currentSummary.categories[catId]?.minutes || 0,
                      color: cat.color
                    };
                  })
                  .filter(item => item.value > 0)
                } 
              />
            </div>
          )}
        </>
      )}

      {["weekly", "monthly", "annual"].includes(activeTab) && (
        <div className="fade">
          <div className="chart-container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, minWidth: 600 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{activeTab === "weekly" ? "Weekly" : activeTab === "monthly" ? "Monthly" : "Annual"} Progress</div>
                <div style={{ fontSize: 13, color: C.muted }}>
                  {activeTab === "weekly" ? `${formatDate(weekRange[0])} - ${formatDate(weekRange[6])}` : activeTab === "monthly" ? format(new Date(selectedDate + "T00:00:00"), "MMMM yyyy") : format(new Date(selectedDate + "T00:00:00"), "yyyy")}
                </div>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <select value={selectType} onChange={e => { setSelectType(e.target.value); setSelectId(""); }} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 13 }}>
                  <option value="category">By Category</option>
                  <option value="task">By Task</option>
                </select>
                <select value={selectId} onChange={e => setSelectId(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 13 }}>
                  <option value="">Select Item</option>
                  {selectType === "category" ? categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>) : Object.keys(currentSummary.tasks).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <LineChart 
              dataPoints={
                activeTab === "weekly" ? weekRange.map(d => weeklyLogs.filter(l => l.date === d && (selectType === "category" ? l.category === selectId : l.topicName === selectId)).reduce((a, c) => a + c.loggedMinutes, 0)) :
                activeTab === "monthly" ? monthRange.map(d => monthlyLogs.filter(l => l.date === d && (selectType === "category" ? l.category === selectId : l.topicName === selectId)).reduce((a, c) => a + c.loggedMinutes, 0)) :
                yearMonths.map(m => annualLogs.filter(l => l.date.startsWith(m) && (selectType === "category" ? l.category === selectId : l.topicName === selectId)).reduce((a, c) => a + c.loggedMinutes, 0))
              }
              labels={
                activeTab === "weekly" ? weekRange.map(d => format(new Date(d + "T00:00:00"), "EEE")) :
                activeTab === "monthly" ? monthRange.map(d => format(new Date(d + "T00:00:00"), "d")) :
                yearMonths.map(m => format(new Date(m + "-01T00:00:00"), "MMM"))
              }
              targetMin={
                activeTab === "annual" ? (selectType === "category" ? (catTargets[selectId]?.targetMinutes || 0) * 25 : (taskTargets[selectId]?.targetMinutes || 0) * 25) : 
                (selectType === "category" ? catTargets[selectId]?.targetMinutes : taskTargets[selectId]?.targetMinutes) || 0
              }
              showTarget={activeTab !== "annual"} // Targets on annual scale are harder to visualize as a flat line
            />
          </div>
          <SummaryTables summary={currentSummary} targetDays={activeTab === "weekly" ? 7 : activeTab === "monthly" ? 30 : 365} />
          
          {Object.keys(currentSummary.categories).some(catId => (currentSummary.categories[catId]?.minutes || 0) > 0) && (
            <div className="chart-container" style={{ marginTop: 24, textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 16 }}>
                Category Distribution ({activeTab === "weekly" ? "Weekly" : activeTab === "monthly" ? "Monthly" : "Annual"})
              </div>
              <PieChart 
                data={Object.keys(currentSummary.categories)
                  .map(catId => {
                    const cat = categories.find(c => c.id === catId) || { label: catId, color: C.muted };
                    return {
                      label: cat.label,
                      value: currentSummary.categories[catId]?.minutes || 0,
                      color: cat.color
                    };
                  })
                  .filter(item => item.value > 0)
                } 
              />
            </div>
          )}
        </div>
      )}

      {editingTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 400, padding: 24, boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>Set {editingTarget.type === 'cat' ? "Category" : "Task"} Target</div>
            <div style={{ fontSize: 14, color: C.muted, marginBottom: 20 }}>Setting daily target for: <strong>{editingTarget.name}</strong></div>
            <label style={{ fontSize: 12, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Target Time (HH:MM)</label>
            <input type="text" defaultValue={editingTarget.value} onBlur={e => editingTarget.value = e.target.value} placeholder="01:30" style={{ width: "100%", padding: "12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 16, outline: "none", marginBottom: 24, fontFamily: "monospace" }} autoFocus />
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setEditingTarget(null)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: `1px solid ${C.border}`, background: "none", color: C.muted, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => handleSaveTarget(editingTarget.type, editingTarget.id, editingTarget.value)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Save Target</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
